import { CompositeDecorator, DraftInlineStyle, EditorState, Modifier, RichUtils } from "draft-js";
import { customStyleMap, fromHtml, getMultilineValue, RichTextValue } from "./common";
import { findLinkDecoratorEntities, LinkDecorator, LinkDecoratorType } from "./Decorators/LinkDecorator";
import { RichtextEditorCommands } from "./commands";

export class EditorUtils {

    public static readonly toggleInlineStyles = (editorState: EditorState, inlineStyles: string[]): EditorState => {
        return inlineStyles.reduce((prev, inlineStyle) => RichUtils.toggleInlineStyle(prev, inlineStyle), editorState);
    }

    public static readonly toggleBlockType = (editorState: EditorState, blockType: string): EditorState => {
        return RichUtils.toggleBlockType(editorState, blockType);
    }

    private static readonly removeLinks = (editorState: EditorState): EditorState => {
        const selection = editorState.getSelection();
        if (!selection.isCollapsed()) {
            editorState = RichUtils.toggleLink(editorState, selection, null);
        }
        return editorState;
    }

    public static readonly clearFormat = (editorState: EditorState): EditorState => {
        editorState = this.removeLinks(editorState);

        const selection = editorState.getSelection();

        // remove font size
        const content = Modifier.setBlockType(editorState.getCurrentContent(), selection, "unstyled");
        const state = EditorState.push(editorState, content, 'change-block-type');

        // remove font markers
        const cleared_content2 = [
            RichtextEditorCommands.bold.style!,
            RichtextEditorCommands.underline.style!,
            RichtextEditorCommands.italic.style!,
            RichtextEditorCommands.strikethrough.style!,
            ...Object.keys(customStyleMap)]
            .reduce((contentState, style) => Modifier.removeInlineStyle(contentState, selection, style), state.getCurrentContent());

        return EditorState.push(state, cleared_content2, 'change-inline-style');
    }

    public static readonly setEditorFocus = (editorState: EditorState): EditorState => {
        return EditorState.acceptSelection(editorState, editorState.getSelection().merge({ hasFocus: true }));
    }

    public static readonly toggleColorStyle = (editorState: EditorState, data: { toggledStyle: string, stylesState: string[] }, allStyles: string[]): EditorState => {
        let newEditorState = this.setEditorFocus(editorState);
        let currentStyles = newEditorState.getCurrentInlineStyle().toArray();
        const selection = newEditorState.getSelection();

        //remove all styles from allStyles
        newEditorState = EditorUtils.toggleInlineStyles(newEditorState, currentStyles.filter(_ => allStyles.includes(_)));

        currentStyles = newEditorState.getCurrentInlineStyle().toArray();

        //restore stylesState
        if (selection.isCollapsed()) {
            const toggled = [
                ...currentStyles.filter(_ => !data.stylesState.includes(_)), // to remove
                ...data.stylesState.filter(_ => !currentStyles.includes(_)), // to add
            ];
            newEditorState = EditorUtils.toggleInlineStyles(newEditorState, toggled);
        }

        if (data.toggledStyle !== "transparent" && !currentStyles.includes(data.toggledStyle)) {
            newEditorState = EditorUtils.toggleInlineStyles(newEditorState, [data.toggledStyle]);
        }

        return newEditorState;
    }

    public static readonly link = (editorState: EditorState, data: any): EditorState => {
        editorState = this.removeLinks(editorState);
        
        if (data) {
            // create link
            const contentStateWithEntity = editorState.getCurrentContent().createEntity(LinkDecoratorType, 'MUTABLE', { url: data });
            const entityKey = contentStateWithEntity.getLastCreatedEntityKey();
            const linkEditorState = EditorState.set(editorState, { currentContent: contentStateWithEntity });
            editorState = RichUtils.toggleLink(linkEditorState, linkEditorState.getSelection(), entityKey);
        }
        return editorState;
    }

    public static readonly hasStyleFromMap = (currentStyle: DraftInlineStyle, styleMap: any): any => {
        const key = Object.keys(styleMap).find(_ => currentStyle.has(_));
        return key ? styleMap[key] : undefined
    }

    public static readonly getLinkUrl = (editorState: EditorState): string | undefined => {
        const selection = editorState.getSelection();
        const contentState = editorState.getCurrentContent();
        const startKey = selection.getStartKey();
        const startOffset = selection.getStartOffset();
        const blockWithLinkAtBeginning = contentState.getBlockForKey(startKey);
        const linkKey = blockWithLinkAtBeginning.getEntityAt(startOffset);
        return linkKey ? contentState.getEntity(linkKey).getData().url : undefined;
    }

    public static readonly buildEditorState = (value?: RichTextValue): EditorState => {
        const decorator = new CompositeDecorator([
            {
                strategy: findLinkDecoratorEntities,
                component: LinkDecorator,
            },
        ]);
        return !value
            ? EditorState.createEmpty(decorator)
            : EditorState.createWithContent(fromHtml(getMultilineValue(value)), decorator);
    }
}